import React, { useEffect, useState } from 'react'
import './nameDialog.css'
import { FolderNode, getNodeById } from '../WooNotes/folders'
import { DataResponseStatusIds, GUID } from '../Services/ServiceTypes'
import ServiceEditor, { IEditorTextInfo } from '../Services/ServiceEditor'
import Util from '../Util'
import { WooNotesButton, WooNotesButtonSize, WooNotesButtonColor } from '../WooNotes/WooNotesButton'
import moment from 'moment'
import { useElementDimensions } from '../hooks/useElementDimensions'

type Props = {
  token: string
  nodeId: GUID | undefined
  folderNodes: FolderNode[]
  onCancel: () => void
  onError: (errorMessage: string) => void
}

enum DateType {
  Today = 0,
  Yesterday = 1,
  ThisWeek = 2,
  LastWeek = 3,
  ThisMonth = 4,
  SomeMonthThisYear = 5,
  SomeYear = 6,
}

function sameDay(d1: Date, d2: Date) {
  return d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate()
}

interface IDateTypeAndValue {
  type: DateType
  value?: number
}

const mapDateToDateTypeAndValue = (
  now: Date,
  yesterday: Date,
  thisWeekStart: Date,
  lastWeekStart: Date,
  thisMonthStart: Date,
  date: Date,
): IDateTypeAndValue => {
  if (sameDay(now, date)) {
    return { type: DateType.Today }
  }

  if (sameDay(yesterday, date)) {
    return { type: DateType.Yesterday }
  }

  if (date >= thisWeekStart) {
    return { type: DateType.ThisWeek }
  }

  if (date >= lastWeekStart) {
    return { type: DateType.LastWeek }
  }

  if (date >= thisMonthStart) {
    return { type: DateType.ThisMonth }
  }

  if (date.getFullYear() === now.getFullYear()) {
    return { type: DateType.SomeMonthThisYear, value: date.getMonth() + 1 }
  }

  return { type: DateType.SomeYear, value: date.getFullYear() }
}

const getDiffForWeekStart = (weekDay: number) => {
  //weekday = 0 sunday (-6)
  //weekday = 1 monday (0)
  //weekday = 2 tuesday (-1)
  //weekday = 3 wednesday (-2)
  //weekday = 4 thursday (-3)
  //weekday = 5 friday (-4)
  //weekday = 6 saturday (-5)

  if (weekDay === 0) return -6
  return -(weekDay - 1)
}

const getDiffForLastWeekStart = (weekDay: number) => {
  return getDiffForWeekStart(weekDay) - 7
}

const mapMonthToTitle = (month: number): string => {
  switch (month) {
    case 1:
      return 'January'
    case 2:
      return 'February'
    case 3:
      return 'March'
    case 4:
      return 'April'
    case 5:
      return 'May'
    case 6:
      return 'June'
    case 7:
      return 'July'
    case 8:
      return 'August'
    case 9:
      return 'September'
    case 10:
      return 'October'
    case 11:
      return 'November'
    case 12:
      return 'December'
    default:
      return '???'
  }
}

const mapYearToTitle = (month: number): string => {
  return String(month)
}

const mapDateTypeAndValueToTitle = (dateTypeAndValue: IDateTypeAndValue) => {
  switch (dateTypeAndValue.type) {
    case DateType.Today:
      return 'Today'
    case DateType.Yesterday:
      return 'Yesterday'
    case DateType.ThisWeek:
      return 'This week'
    case DateType.LastWeek:
      return 'Last week'
    case DateType.ThisMonth:
      return 'This month'
    case DateType.SomeMonthThisYear:
      return mapMonthToTitle(dateTypeAndValue.value!)
    case DateType.SomeYear:
      return mapYearToTitle(dateTypeAndValue.value!)
  }
}

const HtmlViewer = ({ children }: { children: any }) => {
  return <div style={{ position:'sticky', top: 0, width: '100%' }} dangerouslySetInnerHTML={{ __html: children }} />
}

export const NodeHistoryDialog = ({ token, nodeId, folderNodes, onCancel, onError }: Props) => {
  const [editorTextInfos, setEditorTextInfos] = useState<IEditorTextInfo[]>([])
  const [loadHowMany, setLoadHowMany] = useState(10)

  useEffect(() => {
    if (!nodeId) return

    const getData = async () => {
      const textInfos = await ServiceEditor.getEditorTextInfos(token, nodeId, loadHowMany)
      if (!textInfos) {
        onError(`Failed to load history`)
        return
      }

      setEditorTextInfos(textInfos.slice(1))
    }

    getData()
  }, [nodeId, loadHowMany])

  const nodeName = nodeId ? getNodeById(folderNodes, nodeId)?.name : undefined

  const { ref: mainRef, dimensions: mainDimensions } = useElementDimensions<HTMLDivElement>([nodeName])
  const [content, setContent] = useState('')
  const [selectedTextId, setSelectedTextId] = useState('')

  //if (nodeId === undefined) return null
  if (nodeName === undefined) return null

  const now = new Date()
  const yesterday = moment(now).subtract(1, 'day').toDate()
  const weekDay = moment(now).day()
  const thisWeekStart = moment(now).add(getDiffForWeekStart(weekDay), 'day').toDate()
  const lastWeekStart = moment(now).add(getDiffForLastWeekStart(weekDay), 'day').toDate()
  const monthDay = moment(now).date()
  const thisMonthStart = moment(now)
    .subtract(monthDay - 1, 'day')
    .toDate()
  const gETI = Util.groupBy(editorTextInfos, i =>
    mapDateTypeAndValueToTitle(mapDateToDateTypeAndValue(now, yesterday, thisWeekStart, lastWeekStart, thisMonthStart, i.Timestamp as Date)),
  )

  return (
    <div ref={mainRef} className="dialog-root">
      <div className="dialog-backdrop" onClick={onCancel} />

      <div className="dialog" style={{ width: mainDimensions.width * 0.9 }}>
        <h3>{mainDimensions.width}Show history for {nodeName}</h3>

        <div className="dialog-content" style={{ display: 'flex' }}>
          <div>
            {editorTextInfos.length === 0 && <div>No history</div>}

            {editorTextInfos.length > 0 && (
              <table>
                {/* <thead>
                <tr>
                  <th>Timestamp</th>
                  <th></th>
                </tr>
              </thead> */}
                <tbody>
                  {Array.from(gETI.entries()).map((g, gIdx) => {
                    const dateAndValue = g[0]
                    const list = g[1]

                    return (
                      <React.Fragment key={`dav${gIdx}`}>
                        <tr>
                          <td colSpan={2}>
                            <b>{dateAndValue}</b>
                          </td>
                        </tr>
                        {list.map(editorTextInfo => {
                          return (
                            <tr key={`editorTextInfo${editorTextInfo.Id}`}>
                              <td>{(editorTextInfo.Timestamp as Date).hhmmddmmyyyy()}</td>
                              <td>
                                <div className="tableactions">
                                  <WooNotesButton
                                    pressed={selectedTextId === editorTextInfo.Id}
                                    size={WooNotesButtonSize.Slim}
                                    color={selectedTextId === editorTextInfo.Id ? WooNotesButtonColor.Yellow : WooNotesButtonColor.Blue}
                                    onClick={async () => {
                                      if (selectedTextId === editorTextInfo.Id) return
                                      setSelectedTextId(editorTextInfo.Id)
                                      const contentResp = await ServiceEditor.getEditorTextContent(token, nodeId!, editorTextInfo.Id)
                                      if (contentResp.Status.Id === DataResponseStatusIds.General_OK) {
                                        const ccc = contentResp.Data as string
                                        setContent(ccc)
                                      } else {
                                        setContent('<b>Unable to read history item</b>')
                                      }
                                      //onInsertAttachment(nodeId, editorAttachment)
                                    }}
                                  >
                                    Inspect
                                  </WooNotesButton>
                                </div>
                              </td>
                            </tr>
                          )
                        })}
                      </React.Fragment>
                    )
                  })}
                  <tr>
                    <td colSpan={2} align="center">
                      <WooNotesButton size={WooNotesButtonSize.Slim} color={WooNotesButtonColor.Green} onClick={() => setLoadHowMany(x => x + 10)}>
                        Load more
                      </WooNotesButton>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
          <HtmlViewer>{content}</HtmlViewer>
        </div>

        <div className="actions">
          <WooNotesButton size={WooNotesButtonSize.Slim} color={WooNotesButtonColor.White} onClick={onCancel}>
            Close
          </WooNotesButton>
        </div>
      </div>
    </div>
  )
}
